import React, { useState, useEffect } from 'react';
import './App.css';

// ==========================================
// CORE PAGES IMPORTS
// ==========================================
import LandingPage from './pages/Landing';
import AboutPage from './pages/AboutUs';
import ContactPage from './pages/Contact';
import PrivacyPolicy from './pages/privacypolicy';
import ResponsiveLoginPage from './pages/hostel/hostellogin';
import StudentRegistration from './pages/StudentRegistration';
import ApplicationStatusChecker from './pages/ApplicationStatusChecker';
import { logActivity, trackModuleEnter } from './utils/activityLogger';

// ==========================================
// HOSTEL DASHBOARD COMPONENTS
// ==========================================
import AdminHostelDashboard from './pages/hostel/admin/adminhosteldashboard';
import StHostelDashboard from './pages/hostel/student/sthosteldashboard';
import SuperAdminDashboard from './pages/hostel/superadmin/superadmindashboard';
import OwnerDashboard from './pages/hostel/owner/ownerdashboard';
import HostelData from './pages/hostel/owner/hosteldata';
import Inventory from './pages/hostel/owner/inventory';

// ==========================================
// HOSTEL SUPERADMIN MANAGEMENT PAGES
// ==========================================
import ManageAdmin from './pages/hostel/superadmin/manageadmin';
import ManageApplication from './pages/hostel/superadmin/manageapplication';
import ManageCheckin from './pages/hostel/superadmin/managecheckin';
import ManageGrievance from './pages/hostel/superadmin/managegrievance';
import ManageHostel from './pages/hostel/superadmin/managehostel';
import ManageRequests from './pages/hostel/superadmin/managerequests';
import ManageRequisition from './pages/hostel/superadmin/managerequisition';
import ManageStudents from './pages/hostel/superadmin/managestudents';
import ManageLaundry from './pages/hostel/superadmin/laundrymanage';
import ManageRooms from './pages/hostel/superadmin/managerooms';
import RentManage from './pages/hostel/superadmin/rentmanage';
import AuditLogs from './pages/hostel/superadmin/AuditLogs';
import BackupRestore from './pages/hostel/superadmin/BackupRestore';
// ==========================================
// HOSTEL ADMIN MANAGEMENT PAGES
// ==========================================
import HostelAttendance from './pages/hostel/admin/attendance';
import HostelPayin from './pages/hostel/admin/payin';
import HostelPaymentDetails from './pages/hostel/admin/paymentdetails';
import HostelRequest from './pages/hostel/admin/request';
import HostelRoom from './pages/hostel/admin/room';
import HostelBilling from './pages/hostel/admin/billing';
import HostelStudent from './pages/hostel/admin/student';
import HostelAdminLaundry from './pages/hostel/admin/laundry';
import HostelLeaveLevel1 from './pages/hostel/admin/leavelevel1';
import HostelLeaveLevel2 from './pages/hostel/admin/leavelevel2';

// ==========================================
// HOSTEL STUDENT PAGES
// ==========================================
import HostelStudentAttendance from './pages/hostel/student/attendance';
import HostelStudentEntryExitLog from './pages/hostel/student/Entry_exit_log';
import HostelStudentFoodPreference from './pages/hostel/student/foodpreference';
import HostelStudentLaundry from './pages/hostel/student/laundry';
import HostelStudentPayments from './pages/hostel/student/payments';
import HostelStudentRequests from './pages/hostel/student/requests';
import HostelStudentRequisition from './pages/hostel/student/requisition';
import HostelStudentRoomDetails from './pages/hostel/student/roomdetails';
import HostelStudentLeaveApply from './pages/hostel/student/leaveapply';

// ==========================================
// HOSTEL MESS MANAGEMENT PAGES
// ==========================================
import MessFood from './pages/hostel/mess/food';
import MessFoodImage from './pages/hostel/mess/foodimage';
import MessDashboard from './pages/hostel/mess/messdashboard';
import InventoryManage from './pages/hostel/mess/inventorymanage';

// ==========================================
// ERROR BOUNDARY COMPONENT
// ==========================================
const ErrorBoundary = ({ children, componentName, onNavigate }) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleError = (error, errorInfo) => {
      console.error(`âŒ Error in ${componentName}:`, error, errorInfo);
      setHasError(true);
      setError(error);
    };

    const handleUnhandledRejection = (event) => {
      console.error(`âŒ Unhandled promise rejection in ${componentName}:`, event.reason);
      setHasError(true);
      setError(new Error('Unhandled promise rejection'));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [componentName]);

  if (hasError) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f7fa',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '600px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', color: '#ff6b6b' }}>âš ï¸</div>
          <h3 style={{
            color: '#dc2626',
            marginBottom: '1rem',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            Component Error
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem', lineHeight: 1.6 }}>
            There was an error loading the <strong>{componentName}</strong> component.
          </p>
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            fontSize: '0.9rem',
            color: '#991b1b',
            textAlign: 'left'
          }}>
            <strong>Error Details:</strong><br />
            {error?.message || 'Unknown error occurred'}
          </div>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                setHasError(false);
                setError(null);
                window.location.reload();
              }}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1e7139'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#238548'}
            >
              ðŸ"„ Reload Page
            </button>
            <button
              onClick={() => onNavigate('home')}
              style={{
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
            >
              ðŸ  Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// ==========================================
// MAIN APP COMPONENT
// ==========================================
function App() {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('currentPage');
    const hashPage = window.location.hash.slice(1);
    return hashPage || savedPage || 'home';
  });

  // Authentication state
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true' ||
    localStorage.getItem('isLoggedIn') === 'true' ||
    localStorage.getItem('userInfo') !== null;

  const userInfo = JSON.parse(
    sessionStorage.getItem('userInfo') ||
    localStorage.getItem('userInfo') ||
    '{}'
  );

  // ==========================================
  // ROUTE DEFINITIONS
  // ==========================================
  const protectedRoutes = {
    'hostel-superadmin': [
      'hostel-superadmin-dashboard',
      'hostel-superadmin-manage-admin',
      'hostel-superadmin-manage-application',
      'hostel-superadmin-manage-checkin',
      'hostel-superadmin-manage-grievance',
      'hostel-superadmin-manage-hostel',
      'hostel-superadmin-manage-requests',
      'hostel-superadmin-manage-requisition',
      'hostel-superadmin-manage-students',
      'hostel-superadmin-manage-laundry',
      'hostel-superadmin-audit-logs',
      'hostel-superadmin-backup'
    ],
    'hostel-admin': [
      'hostel-admin-dashboard',
      'hostel-admin-students',
      'hostel-admin-room',
      'hostel-admin-payment-details',
      'hostel-admin-request',
      'hostel-admin-attendance',
      'hostel-admin-payin',
      'hostel-admin-billing',
      'hostel-admin-laundry',
      'hostel-admin-leavelevel1',
      'hostel-admin-leavelevel2'
    ],
    'hostel-student': [
      'hostel-student-dashboard',
      'hostel-student-attendance',
      'hostel-student-entry-exit-log',
      'hostel-student-food-preference',
      'hostel-student-laundry',
      'hostel-student-payments',
      'hostel-student-requests',
      'hostel-student-requisition',
      'hostel-student-room-details',
      'hostel-student-leaveapply'
    ],
    'hostel-messmanager': [
      'hostel-mess-dashboard',
      'hostel-mess-food',
      'hostel-mess-foodimage',
      'hostel-mess-inventory-manage'
    ]
  };

  const publicPages = [
    'home',
    'about',
    'contact',
    'privacy-policy',
    'hostel-login',
    'student-register',
    'application-status'
  ];

  const allProtectedPages = Object.values(protectedRoutes).flat();

  const roleDashboards = {
    'hostel-superadmin': 'hostel-superadmin-dashboard',
    'hostel-admin': 'hostel-admin-dashboard',
    'hostel-student': 'hostel-student-dashboard',
    'hostel-messmanager': 'hostel-mess-dashboard'
  };

  // ==========================================
  // NAVIGATION HANDLER
  // ==========================================
  const handleNavigation = (page) => {
    console.log(`ðŸ"„ Navigating to: ${page}`);

    // Log activity - track module enter with time tracking
    trackModuleEnter(page);

    // Update state and storage
    setCurrentPage(page);
    localStorage.setItem('currentPage', page);

    // Update browser history
    if (window.history.pushState) {
      window.history.pushState({ page }, '', `#${page}`);
    } else {
      window.location.hash = page;
    }

    // Clear authentication on logout/login page
    if (page === 'hostel-login' || page === 'home') {
      console.log('ðŸ§¹ Clearing authentication data');
      sessionStorage.clear();
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('token');

      if (page === 'home') {
        localStorage.removeItem('currentPage');
      }
    }

    // Scroll to top on navigation
    window.scrollTo(0, 0);
  };

  // ==========================================
  // EFFECTS - BROWSER NAVIGATION
  // ==========================================
  useEffect(() => {
    const handlePopState = (event) => {
      const page = event.state?.page || window.location.hash.slice(1) || 'home';
      console.log(`ðŸ"™ Browser back/forward navigation: ${page}`);

      if (isLoggedIn || publicPages.includes(page)) {
        setCurrentPage(page);
        localStorage.setItem('currentPage', page);
      } else {
        setCurrentPage('hostel-login');
        localStorage.setItem('currentPage', 'hostel-login');
      }
    };

    const handleHashChange = () => {
      const page = window.location.hash.slice(1) || 'home';
      console.log(`ðŸ"— Hash change detected: ${page}`);

      if (page !== currentPage) {
        setCurrentPage(page);
        localStorage.setItem('currentPage', page);
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);

    // Initialize hash if not present
    if (!window.location.hash && currentPage !== 'home') {
      window.location.hash = currentPage;
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isLoggedIn, currentPage]);

  // ==========================================
  // EFFECTS - CLICK TRACKING (AUDIT)
  // ==========================================
  useEffect(() => {
    const handleClick = (e) => {
      // Track interactive elements
      const target = e.target.closest('button, a, input, select, [role="button"], .sidebar-menu-item, .stat-card, .nav-link, .tab-button');

      if (target) {
        const text = target.innerText || target.value || target.getAttribute('aria-label') || target.id || '';
        const tagName = target.tagName;
        const href = target.getAttribute('href') || null;
        const dataAction = target.getAttribute('data-action') || null;

        // Try to find the closest section for context
        const section = target.closest('section, [class*="sidebar"], [class*="modal"], [class*="header"], .main-content');
        const sectionName = section ? (section.getAttribute('aria-label') || section.className?.split(' ')[0] || '') : '';

        logActivity('CLICK', {
          text: (text || '').slice(0, 80),
          tag: tagName,
          id: target.id,
          className: (target.className || '').toString().slice(0, 60),
          href,
          dataAction,
          section: sectionName.slice(0, 40),
          page: currentPage
        });
      }
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [currentPage]);

  // ==========================================
  // EFFECTS - AUTHENTICATION CHECK
  // ==========================================
  useEffect(() => {
    const checkAuth = () => {
      console.log(`ðŸ" Checking authentication for page: ${currentPage}`);
      console.log(`ðŸ"Š User role: ${userInfo.role || 'Not logged in'}`);
      console.log(`ðŸ"" Is logged in: ${isLoggedIn}`);

      // Redirect to login if accessing protected page without auth
      if (allProtectedPages.includes(currentPage) && !isLoggedIn) {
        console.log('🔒 Redirecting to login - not authenticated');
        handleNavigation('hostel-login');
        return;
      }

      // Handle authenticated user trying to access login page
      if (isLoggedIn && userInfo.role && currentPage === 'hostel-login') {
        console.log('ðŸ  Redirecting to dashboard - already authenticated');
        const targetDashboard = roleDashboards[userInfo.role] || 'home';
        handleNavigation(targetDashboard);
        return;
      }

      // Check role-based access for protected pages
      if (isLoggedIn && userInfo.role) {
        const currentRole = userInfo.role;
        const userRoutes = protectedRoutes[currentRole] || [];

        if (allProtectedPages.includes(currentPage) && !userRoutes.includes(currentPage)) {
          console.log(`ðŸš« Access denied - ${currentRole} cannot access ${currentPage}`);
          const targetDashboard = roleDashboards[currentRole] || 'home';
          handleNavigation(targetDashboard);
          return;
        }
      }
    };

    checkAuth();
  }, [currentPage, isLoggedIn, userInfo.role]);

  // ==========================================
  // COMPONENT WRAPPER
  // ==========================================
  const ComponentWrapper = ({ children, componentName }) => {
    return (
      <ErrorBoundary componentName={componentName} onNavigate={handleNavigation}>
        {children}
      </ErrorBoundary>
    );
  };

  // ==========================================
  // AUTHENTICATION GUARD
  // ==========================================
  const requireAuth = (requiredRole, component) => {
    if (!isLoggedIn) {
      console.log('âš ï¸ Authentication required - redirecting to login');
      return <ResponsiveLoginPage onNavigate={handleNavigation} />;
    }

    if (requiredRole && userInfo.role !== requiredRole) {
      console.log(`âš ï¸ Role mismatch - required: ${requiredRole}, actual: ${userInfo.role}`);
      return <ResponsiveLoginPage onNavigate={handleNavigation} />;
    }

    return component;
  };

  // ==========================================
  // PAGE RENDERER
  // ==========================================
  const renderCurrentPage = () => {
    console.log(`ðŸŽ¨ Rendering page: ${currentPage}`);

    switch (currentPage) {
      // ==========================================
      // PUBLIC PAGES
      // ==========================================
      case 'home':
        return (
          <ComponentWrapper componentName="LandingPage">
            <LandingPage onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      case 'about':
        return (
          <ComponentWrapper componentName="AboutPage">
            <AboutPage onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      case 'contact':
        return (
          <ComponentWrapper componentName="ContactPage">
            <ContactPage onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      case 'privacy-policy':
        return (
          <ComponentWrapper componentName="PrivacyPolicy">
            <PrivacyPolicy onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      case 'student-register':
        return (
          <ComponentWrapper componentName="StudentRegistration">
            <StudentRegistration onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      case 'application-status':
        return (
          <ComponentWrapper componentName="ApplicationStatusChecker">
            <ApplicationStatusChecker onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      case 'hostel-login':
        return (
          <ComponentWrapper componentName="ResponsiveLoginPage">
            <ResponsiveLoginPage onNavigate={handleNavigation} />
          </ComponentWrapper>
        );

      // ==========================================
      // HOSTEL SUPERADMIN ROUTES
      // ==========================================
      case 'hostel-superadmin-dashboard':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="SuperAdminDashboard">
            <SuperAdminDashboard onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-admin':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageAdmin">
            <ManageAdmin onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-application':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageApplication">
            <ManageApplication onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-checkin':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageCheckin">
            <ManageCheckin onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-grievance':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageGrievance">
            <ManageGrievance onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-hostel':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageHostel">
            <ManageHostel onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-requests':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageRequests">
            <ManageRequests onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-requisition':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageRequisition">
            <ManageRequisition onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-students':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageStudents">
            <ManageStudents onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-audit-logs':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="AuditLogs">
            <AuditLogs onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-backup':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="BackupRestore">
            <BackupRestore onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-laundry':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageLaundry">
            <ManageLaundry onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-superadmin-manage-rooms':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="ManageRooms">
            <ManageRooms onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );
      case 'hostel-superadmin-rent':
        return requireAuth('hostel-superadmin',
          <ComponentWrapper componentName="RentManage">
            <RentManage onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );
      // ==========================================
      // HOSTEL OWNER ROUTES
      // ==========================================
      case 'hostel-owner-dashboard':
        return requireAuth('hostel-owner',
          <ComponentWrapper componentName="OwnerDashboard">
            <OwnerDashboard onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-owner-hosteldata':
        return requireAuth('hostel-owner',
          <ComponentWrapper componentName="HostelData">
            <HostelData onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-owner-inventory':
        return requireAuth('hostel-owner',
          <ComponentWrapper componentName="Inventory">
            <Inventory onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      // ==========================================
      // HOSTEL ADMIN ROUTES
      // ==========================================
      case 'hostel-admin-dashboard':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="AdminHostelDashboard">
            <AdminHostelDashboard onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-students':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelStudent">
            <HostelStudent onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-room':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelRoom">
            <HostelRoom onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-payment-details':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelPaymentDetails">
            <HostelPaymentDetails onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-request':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelRequest">
            <HostelRequest onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-attendance':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelAttendance">
            <HostelAttendance onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-payin':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelPayin">
            <HostelPayin onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-billing':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelBilling">
            <HostelBilling onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-laundry':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelAdminLaundry">
            <HostelAdminLaundry onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-leavelevel1':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelLeaveLevel1">
            <HostelLeaveLevel1 onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-admin-leavelevel2':
        return requireAuth('hostel-admin',
          <ComponentWrapper componentName="HostelLeaveLevel2">
            <HostelLeaveLevel2 onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      // ==========================================
      // HOSTEL STUDENT ROUTES
      // ==========================================
      case 'hostel-student-dashboard':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="StHostelDashboard">
            <StHostelDashboard onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-attendance':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentAttendance">
            <HostelStudentAttendance onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-entry-exit-log':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentEntryExitLog">
            <HostelStudentEntryExitLog onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-food-preference':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentFoodPreference">
            <HostelStudentFoodPreference onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-laundry':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentLaundry">
            <HostelStudentLaundry onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-payments':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentPayments">
            <HostelStudentPayments onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-requests':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentRequests">
            <HostelStudentRequests onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-requisition':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentRequisition">
            <HostelStudentRequisition onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-room-details':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentRoomDetails">
            <HostelStudentRoomDetails onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-student-leaveapply':
        return requireAuth('hostel-student',
          <ComponentWrapper componentName="HostelStudentLeaveApply">
            <HostelStudentLeaveApply onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      // ==========================================
      // HOSTEL MESS MANAGER ROUTES
      // ==========================================
      case 'hostel-mess-dashboard':
        return requireAuth('hostel-messmanager',
          <ComponentWrapper componentName="MessDashboard">
            <MessDashboard onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-mess-food':
        return requireAuth('hostel-messmanager',
          <ComponentWrapper componentName="MessFood">
            <MessFood onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-mess-foodimage':
        return requireAuth('hostel-messmanager',
          <ComponentWrapper componentName="MessFoodImage">
            <MessFoodImage onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      case 'hostel-mess-inventory-manage':
        return requireAuth('hostel-messmanager',
          <ComponentWrapper componentName="InventoryManage">
            <InventoryManage onNavigate={handleNavigation} userInfo={userInfo} />
          </ComponentWrapper>
        );

      // ==========================================
      // DEFAULT FALLBACK
      // ==========================================
      default:
        console.log(`â" Unknown page: ${currentPage}, redirecting to home`);
        return (
          <ComponentWrapper componentName="LandingPage">
            <LandingPage onNavigate={handleNavigation} />
          </ComponentWrapper>
        );
    }
  };

  // ==========================================
  // RENDER APP
  // ==========================================
  return (
    <div className="App">
      {renderCurrentPage()}
    </div>
  );
}

export default App;